import classNames from 'classnames';
import { ComponentProps, ReactNode, useContext, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import Typography from '@alltrails/core/components/Typography';
import useAnimateInOutComponent from '@alltrails/core/hooks/useAnimateInOutComponent';
import useScroll from '@alltrails/core/hooks/useScroll';
import IconButton from '@alltrails/denali/components/IconButton';
import PagedProgressBar from '@alltrails/denali/components/PagedProgressBar';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import ArrowSq from '@alltrails/denali/icons/ArrowSq';
import Close from '@alltrails/denali/icons/Close';
import FooterButtons from '../FooterButtons';
import { ModalAriaLabelledByContext } from './ModalAriaLabelledByContext';
import { ModalContext } from './MultiStepModal';
import styles from './styles/Modal.module.scss';

type ModalProps = {
  buttons?: Pick<ComponentProps<typeof FooterButtons>, 'fill' | 'defaultButton' | 'flatButton' | 'primaryButton' | 'experimental'>;
  caption?: ReactNode;
  children: ReactNode;
  contentsClassName?: string;
  titleClassName?: string;
  footerClassName?: string;
  onBackRequest?: () => void;
  onCloseComplete?: () => void;
  onCloseRequest: () => void;
  onScroll?: () => void;
  shouldHideHeader?: boolean;
  scrollContainerClassName?: string;
  hideBackButton?: boolean;
  hideCloseButton?: boolean;
  pageInfo?: Pick<ComponentProps<typeof PagedProgressBar>, 'index' | 'total'>;
} & (
  | {
      headerImage?: never;
      headerImageV2?: never;
      subtitle?: ReactNode;
      title?: ReactNode;
    }
  | {
      headerImage?: ReactNode;
      headerImageV2?: boolean;
      subtitle?: never;
      title?: never;
    }
);

// https://www.figma.com/design/9Y1n3VoMJAcTGPe82bvySj/Denali-Components?node-id=3084-40503
const ModalStep = ({
  buttons,
  caption,
  children,
  contentsClassName,
  headerImage,
  titleClassName,
  footerClassName,
  onBackRequest: stepBackRequest,
  onCloseRequest,
  onCloseComplete,
  onScroll,
  pageInfo,
  subtitle,
  title,
  shouldHideHeader = false,
  scrollContainerClassName,
  hideBackButton = false,
  hideCloseButton = false,
  headerImageV2 = false
}: ModalProps) => {
  const { isOpen, onBackRequest: modalBackRequest } = useContext(ModalContext);
  const ariaLabelledById = useContext(ModalAriaLabelledByContext);

  const intl = useIntl();
  const isMobile = useIsMobileSizedScreen();
  const { isVisible } = useAnimateInOutComponent(isOpen, onCloseComplete);
  const scrollContainer = useRef<HTMLDivElement>(null);
  const headerImageContainer = useRef<HTMLDivElement>(null);
  const { amountScrolled, canScrollDown, canScrollUp, updateScroll } = useScroll(scrollContainer, true, isVisible);

  const isImageModal = useMemo(() => Boolean(headerImage), [headerImage]);

  const titleElement = useMemo(() => {
    if (title) {
      return (
        <div className={classNames(styles.titleContainer, titleClassName)}>
          <Typography className={styles.title} component="h2" id={ariaLabelledById} variant={isMobile ? 'heading300' : 'heading400'}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="text200" color="secondary">
              {subtitle}
            </Typography>
          )}
        </div>
      );
    }
    return null;
  }, [ariaLabelledById, isMobile, subtitle, title, titleClassName]);

  const showFooter = Boolean(buttons || pageInfo);

  // When the image has been scrolled half out of view, we want to start making the header opaque rather than transparent.
  // We do this gradually until the entire image has been scrolled out of view, at which point the header will be completely opaque.
  const [didReachScrollOpacityThreshold, setDidReachScrollOpacityThreshold] = useState(false);

  const headerStyle = useMemo(() => {
    if (isImageModal && headerImageContainer.current) {
      const imageHeight = headerImageContainer.current.clientHeight;
      const halfImageHeight = imageHeight / 2;
      const reachedThreshold = amountScrolled > halfImageHeight;

      if (reachedThreshold && !didReachScrollOpacityThreshold) {
        setDidReachScrollOpacityThreshold(true);
      } else if (!reachedThreshold && didReachScrollOpacityThreshold) {
        setDidReachScrollOpacityThreshold(false);
      }

      let opacity: number;
      if (amountScrolled >= imageHeight) {
        opacity = 100;
      } else if (amountScrolled <= halfImageHeight) {
        opacity = 0;
      } else {
        opacity = 100 * ((amountScrolled - halfImageHeight) / halfImageHeight);
      }

      return { backgroundColor: `color-mix(in srgb, currentColor ${opacity}%, transparent)` };
    }
    return undefined;
  }, [amountScrolled, didReachScrollOpacityThreshold, isImageModal]);

  const getCloseButtonVariant = (isImageModal: boolean, headerImageV2: boolean) => {
    if (isImageModal && !headerImageV2) {
      return 'elevated';
    }
    if (headerImageV2) {
      return didReachScrollOpacityThreshold ? 'default' : 'elevated';
    }
    return 'default';
  };

  const onBackRequest = useMemo(
    () =>
      modalBackRequest || stepBackRequest
        ? () => {
            modalBackRequest?.();
            stepBackRequest?.();
          }
        : undefined,
    [modalBackRequest, stepBackRequest]
  );

  return (
    <>
      {!shouldHideHeader && (
        <div
          className={classNames(styles.header, {
            [styles.imageModal]: isImageModal,
            [styles.imageModalV2]: headerImageV2,
            [styles.bottomBorder]: canScrollUp
          })}
          style={headerStyle}
        >
          {onBackRequest && !hideBackButton ? (
            <IconButton
              icon={{ Component: ArrowSq, orientation: 'left' }}
              onClick={onBackRequest}
              size="sm"
              title={intl.formatMessage({ defaultMessage: 'Go back' })}
              variant={isImageModal ? 'elevated' : 'default'}
            />
          ) : (
            <div className={styles.buttonPlaceholder} />
          )}
          {isMobile && titleElement}
          {!hideCloseButton && (
            <IconButton
              icon={{ Component: Close }}
              onClick={onCloseRequest}
              size="sm"
              title={intl.formatMessage({ defaultMessage: 'Close' })}
              variant={getCloseButtonVariant(isImageModal, headerImageV2)}
            />
          )}
        </div>
      )}
      <div
        className={classNames(styles.scrollContainer, scrollContainerClassName, { [styles.headerImageV2]: headerImageV2 })}
        onScroll={() => {
          onScroll?.();
          updateScroll();
        }}
        ref={scrollContainer}
        id="modal-scroll-container"
      >
        {isImageModal && (
          <div className={styles.headerImageContainer} ref={headerImageContainer}>
            {headerImage}
          </div>
        )}
        <div className={classNames(styles.contents, contentsClassName, { [styles.experimentalFooter]: buttons?.experimental })}>
          {!isMobile && titleElement}
          {children}
        </div>
      </div>
      {showFooter && (
        <div
          className={classNames(styles.footer, footerClassName, {
            [styles.topBorder]: canScrollDown,
            [styles.experimentalFooterContainer]: buttons?.experimental
          })}
        >
          {pageInfo && <PagedProgressBar className={styles.pagedProgressBar} {...pageInfo} />}
          {buttons && <FooterButtons {...buttons} fill={buttons.fill || isMobile} />}
          {caption && <div className={styles.caption}>{caption}</div>}
        </div>
      )}
    </>
  );
};

export default ModalStep;
